// COo Imports
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  Spinner,
  Box,
  Avatar,
  Flex,
  useToast, // Keep useToast if needed, though Store is logic-heavy now
} from "components-lib";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { BiSupport } from "react-icons/bi";
import { useChat } from "contexts/ChatContext";
import { axiosInstance, getAuthHeaders } from "config/api";

/**
 * Reusable Chat Modal Component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Function to close the modal
 * @param {Object} props.participant - Participant object with sender info
 * @param {string} props.adminId - Admin ID (default: 1)
 * @param {string} props.adminType - Admin type (default: 'admin')
 */
export default function ChatModal({
  isOpen,
  onClose,
  participant = null,
  adminId = 1, // Kept for prop compatibility, but Context handles user
  adminType = 'admin'
}) {
  const { 
    chatState, 
    sendMessage, 
    openChat, 
    closeChat, 
    currentUser 
  } = useChat();

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const markReadSentRef = useRef(false);

  // Chat state
  const [newMessage, setNewMessage] = useState("");
  const [hasUnread, setHasUnread] = useState(false);
  
  // Resolve Chat ID
  const chatId = participant ? (participant.sender_id || participant.id || 'unknown').toString() : null;

  // Effects
  useEffect(() => {
    if (isOpen && chatId) {
      // Capture hasUnread before openChat clears it
      const currentHasUnread = (chatState.chats[chatId]?.unseenCount || 0) > 0;
      setHasUnread(currentHasUnread);
      openChat(chatId);
    } else {
      closeChat();
      setHasUnread(false);
    }
  }, [isOpen, chatId]);

  // Reset mark-read guard when modal closes or chatId changes
  useEffect(() => {
    if (!isOpen) markReadSentRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    markReadSentRef.current = false;
  }, [chatId]);

  // When modal opens, mark messages as read on the server for this chat
  useEffect(() => {
    if (!isOpen || !chatId || !participant) return;
    if (markReadSentRef.current) return; // guard: only send once per open

    const sendMarkRead = async () => {
      // Resolve sender (the other participant) and receiver (current user)
      const senderId = participant.sender_id || participant.id || null;
      const senderType = (participant.sender_type || participant.type || 'user').toString();

      const receiverId = currentUser?.id || currentUser?.userId || adminId || null;
      const receiverType = (currentUser?.role || currentUser?.type || adminType || 'user').toString();

      if (!senderId || !receiverId) return;

      const headers = getAuthHeaders();

      // mark as sent immediately to avoid duplicate concurrent requests
      markReadSentRef.current = true;

      try {
        const payload = {
          sender_id: Number(senderId),
          sender_type: senderType,
          receiver_id: Number(receiverId),
          receiver_type: receiverType,
        };

        // Use shared axios instance so baseURL and auth are applied from env/config
        const { data: json } = await axiosInstance.post('api/chat/mark-read', payload, { headers });
        // eslint-disable-next-line no-console
        console.debug('mark-read response', json);
        setHasUnread(false);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('mark-read error', err);
        // allow retry later if it failed
        markReadSentRef.current = false;
      }
    };

    sendMarkRead();
  }, [isOpen, chatId, participant, currentUser]);

  // Derived state from Store
  const messages = useMemo(() => {
    if (!chatId || !chatState.chats[chatId]) return [];
    return chatState.chats[chatId].messages || [];
  }, [chatState, chatId]);

  // Scroll to bottom
  useEffect(() => {
    if (isOpen) {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
    }
  }, [messages, isOpen]);


  const handleSendMessage = () => {
    if (!newMessage.trim() || !chatId) return;
    sendMessage(chatId, newMessage.trim(), participant.sender_type);
    setNewMessage("");
  };

  // Helper for highlighting/visuals
  const getMessageStyles = (msg) => {
    const isMe = msg.isMe; // Trust the store's "isMe"
    const shouldHighlight = hasUnread && !isMe;
    
    return {
        bg: shouldHighlight ? "yellow.100" : "white",
        color: isMe ? "grey.400" : "gray.800",
        align: isMe ? "flex-end" : "flex-start",
        fontWeight: shouldHighlight ? "bold" : "normal",
        border: shouldHighlight ? "2px solid #3182ce" : "none"
    };
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    try {
        return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) { return ''; }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent maxH="80vh" display="flex" flexDirection="column">
        <ModalHeader>
          {participant && (
            <Flex alignItems="center">
              <Avatar name={participant.sender_name} size="sm" mr={3} />
              <Box>
                <Text fontSize="lg" fontWeight="bold">
                  {participant.sender_name}
                </Text>
                <Text fontSize="sm" color="gray.500" textTransform="capitalize">
                    {(participant.sender_type || participant.type || 'User').replace('_', ' ')}
                </Text>
              </Box>
            </Flex>
          )}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody flex="1" p={4} minH={0} bg="gray.50">
           <Box display="flex" flexDirection="column" gap={3} px={2} overflowY="auto" h="100%" maxH="60vh">
             {messages.length === 0 ? (
                <Flex justify="center" align="center" h="100%" flexDirection="column" color="gray.400" mt={10}>
                    <BiSupport size="48px" />
                    <Text mt={4}>No messages yet. Start the conversation!</Text>
                </Flex>
             ) : (
                 messages.map((msg) => {
                     const styles = getMessageStyles(msg);
                     return (
                        <Box
                            key={msg.id}
                            bg={styles.bg}
                            color={styles.color}
                            p={3}
                            borderRadius="lg"
                            maxW="70%"
                            alignSelf={styles.align}
                            boxShadow="sm"
                            position="relative"
                            fontWeight={styles.fontWeight}
                            border={styles.border}
                        >
                             <Text fontSize="sm" fontWeight={styles.fontWeight} overflowWrap="break-word">{msg.content}</Text>
                             <HStack justify="flex-end" spacing={1} mt={1}>
                                <Text fontSize="xs" opacity={0.8}>
                                    {formatMessageTime(msg.timestamp)}
                                </Text>
                                {msg.isMe && (
                                    <Text fontSize="xs" fontWeight="bold">
                                        {msg.status === 'sent' && '✓'}
                                        {msg.status === 'delivered' && '✓✓'}
                                        {/* {msg.status === 'seen' && <Box as="span" color="blue.200">✓✓</Box>} */}
                                    </Text>
                                )}
                             </HStack>
                        </Box>
                     );
                 })
             )}
             <div ref={messagesEndRef} />
           </Box>
        </ModalBody>

        <ModalFooter p={4} bg="white">
          <HStack w="100%" spacing={3}>
            <Input
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <Button
              colorScheme="brand"
              onClick={handleSendMessage}
              isDisabled={!newMessage.trim()}
            >
              Send
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}